/**
 * 타일 소스 정의.
 *
 * 소스를 상수로 분리해 둔 이유는 **키 확보가 개발을 막지 않게** 하기 위해서다.
 * 지금은 키 없이 되는 Esri/OSM 으로 붙이고, VWorld 키가 나오면 `VWORLD_*` 의
 * `{key}` 만 채워 기본값을 바꾸면 된다. 컴포넌트는 손대지 않는다.
 *
 * 카카오·네이버는 여기 넣을 수 없다 — 타일 직접 호출을 약관이 금지하며 SDK 로만 쓸 수 있다.
 */

export interface TileSource {
  url: string;
  /** 지도에 반드시 노출해야 하는 출처 표기. */
  attribution: string;
  maxZoom: number;
  /** 위성/항공 영상 여부 — 라벨 오버레이를 얹을지 판단에 쓴다. */
  imagery: boolean;
}

/** 키 불필요. 전 세계 위성·항공 영상. */
export const ESRI_IMAGERY: TileSource = {
  url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  attribution: "Esri, Maxar, Earthstar Geographics",
  maxZoom: 19,
  imagery: true,
};

/** 키 불필요. 도로·지명 위주. */
export const OSM: TileSource = {
  url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution: "&copy; OpenStreetMap contributors",
  maxZoom: 19,
  imagery: false,
};

/** 키 필요 (VITE_VWORLD_KEY) + Kakao 와 달리 표준 XYZ 라 Leaflet 에 그대로 얹힌다. */
const VWORLD_KEY: string = import.meta.env.VITE_VWORLD_KEY ?? "";

export function hasVworldKey(): boolean {
  return VWORLD_KEY.trim().length > 0;
}

export const VWORLD_SATELLITE: TileSource = {
  url: `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Satellite/{z}/{y}/{x}.jpeg`,
  attribution: "국토지리정보원 VWorld",
  maxZoom: 19,
  imagery: true,
};

export const VWORLD_BASE: TileSource = {
  url: `https://api.vworld.kr/req/wmts/1.0.0/${VWORLD_KEY}/Base/{z}/{y}/{x}.png`,
  attribution: "국토지리정보원 VWorld",
  maxZoom: 19,
  imagery: false,
};

/**
 * 현재 사용할 소스. VWorld 키가 있으면 국내 데이터를 쓰고, 없으면 키 없이 되는 쪽으로 떨어진다.
 * 어느 쪽이든 지도는 뜬다 — 키 유무가 기능을 막지 않는다.
 *
 * 주의: 키 "존재"만 보므로 만료·쿼터초과는 여기서 알 수 없다. 그건 런타임에
 * 타일 실패를 세어 처리한다(`addTileLayerWithFallback`) — VWorld 는 인증키에 사용
 * 기간이 있어 갱신을 놓치면 만료되는데, 그때 지도가 통째로 죽으면 안 된다.
 */
export function imagerySource(): TileSource {
  return hasVworldKey() ? VWORLD_SATELLITE : ESRI_IMAGERY;
}

export function baseSource(): TileSource {
  return hasVworldKey() ? VWORLD_BASE : OSM;
}

/** 키가 필요 없어 항상 동작하는 소스 — 폴백 대상. */
export const IMAGERY_FALLBACK = ESRI_IMAGERY;
export const BASE_FALLBACK = OSM;
